import { House, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";

import { CartDrawer } from "@/components/layout/CartDrawer";
import { useAppStore } from "@/lib/store";

/* ── Phosphor-style icons (matching Webflow production) ─────────────── */

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M224,128a8,8,0,0,1-8,8H59.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L59.31,120H216A8,8,0,0,1,224,128Z" />
    </svg>
  );
}

function MagnifyingGlassIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M9.25 9.25V4.75H10.75V9.25H15.25V10.75H10.75V15.25H9.25V10.75H4.75V9.25H9.25Z" fill="white" />
    </svg>
  );
}

const topbarIconButtonClassName =
  "inline-flex h-10 w-10 items-center justify-center rounded-[0.9rem] border border-[#e2e4e9] bg-white text-[#4a193c] transition-colors hover:bg-[#faf5f8] max-[479px]:h-9 max-[479px]:w-9 max-[479px]:rounded-[0.8rem]";
const topbarActionIconClassName = "h-5 w-5 shrink-0 max-[479px]:h-[1.1rem] max-[479px]:w-[1.1rem]";
const topbarActionIconStrokeWidth = 2.1;
const topbarSearchFieldClassName =
  "flex h-[2.75rem] min-w-0 items-center gap-[0.6rem] rounded-[0.8rem] border border-[#ddd9dc] bg-white px-3.5 max-[479px]:h-[2.55rem] max-[479px]:px-3";

/* ── Component ────────────────────────────────────────────────────────── */

interface AppTopbarProps {
  title: string;
  subtitle?: string;
  backTo?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
}

export function AppTopbar({
  title,
  subtitle,
  backTo,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search",
}: AppTopbarProps) {
  const { cartItems, setCartOpen } = useAppStore();

  return (
    <>
      <header className="border-b border-[#e2e4e9] bg-white">
        <div className="flex min-h-[4.5rem] flex-wrap items-center gap-x-4 gap-y-3 px-[clamp(1rem,3vw,3rem)] py-3 max-[767px]:min-h-[4rem] max-[767px]:px-4 max-[479px]:gap-y-2.5 max-[479px]:px-3.5">
          {/* Left: title + subtitle */}
          <div className="flex min-w-0 flex-1 items-center gap-3 max-[479px]:basis-full">
            {backTo ? (
              <Link
                to={backTo}
                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[#e2e4e9] bg-white text-[#525866]"
                aria-label="Back"
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </Link>
            ) : null}

            <div className="min-w-0">
              <div className="text-[1rem] font-semibold leading-[1.2] text-black max-[479px]:text-[0.95rem]">
                {title}
              </div>
              {subtitle ? (
                <div className="text-[0.875rem] text-[#666] max-[479px]:hidden">
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>

          {/* Right: search + button + cart + home */}
          <div className="ml-auto flex shrink-0 items-center gap-4 max-[767px]:gap-2 max-[479px]:w-full max-[479px]:justify-end">
            {/* Desktop search (hidden on tablet) */}
            <label
              className={`${topbarSearchFieldClassName} w-72 max-[991px]:hidden`}
              aria-label="Search"
            >
              <MagnifyingGlassIcon className="h-[0.9rem] w-[0.9rem] flex-none text-[#80787d]" />
              <input
                type="search"
                value={searchValue}
                placeholder={searchPlaceholder}
                className="w-full border-0 bg-transparent text-[0.9rem] text-[#282126] outline-0 placeholder:text-[#8d888c]"
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </label>

            {/* Buy New Tutorials (hidden on <=767px) */}
            <Link
              to="/dance-library"
              className="inline-flex items-center justify-center gap-2 rounded-[1rem] bg-[#762a5c] px-5 py-4 text-[0.875rem] font-medium leading-none text-white whitespace-nowrap transition-transform duration-200 hover:-translate-y-[5%] active:scale-95 max-[767px]:hidden"
            >
              <PlusIcon className="h-[1.25rem] w-[1.25rem]" />
              <span>Buy New Tutorials</span>
            </Link>

            <button
              type="button"
              className={`relative ${topbarIconButtonClassName}`}
              aria-label="Open cart"
              onClick={() => setCartOpen(true)}
            >
              <ShoppingCart className={topbarActionIconClassName} strokeWidth={topbarActionIconStrokeWidth} />
              {cartItems.length > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-[#762a5c] px-1.5 py-0.5 text-[0.6875rem] font-bold leading-none text-white max-[479px]:-top-1 max-[479px]:-right-1 max-[479px]:min-w-[1.125rem] max-[479px]:px-1 max-[479px]:text-[0.625rem]">
                  {cartItems.length}
                </span>
              ) : null}
            </button>

            {/* Home button — visible only on mobile, links to dashboard home */}
            <Link
              to="/app/dashboard/home"
              className={`${topbarIconButtonClassName} min-[992px]:hidden`}
              aria-label="Home"
            >
              <House className={topbarActionIconClassName} strokeWidth={topbarActionIconStrokeWidth} />
            </Link>
          </div>

          <label
            className={`${topbarSearchFieldClassName} hidden basis-full max-[991px]:flex`}
            aria-label="Search"
          >
            <MagnifyingGlassIcon className="h-[0.9rem] w-[0.9rem] flex-none text-[#80787d]" />
            <input
              type="search"
              value={searchValue}
              placeholder={searchPlaceholder}
              className="w-full border-0 bg-transparent text-[0.9rem] text-[#282126] outline-0 placeholder:text-[#8d888c]"
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </label>
        </div>
      </header>
      <CartDrawer />
    </>
  );
}
